import { useEffect, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { createVariable } from '../api';
import { invalidateLookup } from '../lookups';
import { HttpError } from '../../api/http';

interface Props {
  open: boolean;
  applicationId: number;
  onCreated: (id: string, name: string) => void;
  onCancel: () => void;
}

/**
 * Small focused modal for creating a new application-scoped variable
 * directly from a variable picker. Replaces the multi-step modal in the
 * legacy designer (modal-variable-create in NodeDetails.cshtml) — the
 * Flow Builder only needs a name; scope defaults to Application server-side.
 *
 * On success we invalidate the variable lookup cache so every other picker
 * in the editor (and the node-card summary resolver) picks the new entry
 * up without a page refresh.
 */
export function VariableCreateModal({ open, applicationId, onCreated, onCancel }: Props) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a name.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createVariable(applicationId, trimmed);
      // Force every active picker + the node-card summary resolver to
      // re-fetch the variable list so the new entry is selectable
      // everywhere immediately.
      invalidateLookup('variable');
      onCreated(String(res.id), res.name || trimmed);
    } catch (ex) {
      const httpEx = ex as HttpError;
      // The API returns 409 with `{ message }` when the name already
      // exists; the Http helper turns that into `statusText`. Show the
      // server message when present, otherwise a sensible fallback.
      const msg =
        (httpEx?.data && typeof httpEx.data === 'object' && 'message' in (httpEx.data as Record<string, unknown>)
          ? String((httpEx.data as { message?: string }).message ?? '')
          : '') ||
        (httpEx?.status === 409 ? 'A variable with that name already exists.' : '') ||
        httpEx?.message ||
        'Could not create variable.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Create variable"
      busy={busy}
      maxWidth={420}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || !name.trim()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: 'grid', gap: 10 }}
      >
        <label style={{ display: 'grid', gap: 4 }}>
          <span
            style={{
              fontSize: '0.8rem',
              color: 'var(--portal-muted)',
              fontWeight: 600,
            }}
          >
            Name
          </span>
          <input
            type="text"
            value={name}
            disabled={busy}
            autoFocus
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            placeholder="e.g. retryCount"
            maxLength={200}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--portal-border)',
              borderRadius: 6,
              fontSize: '0.95rem',
              fontFamily: 'inherit',
              background: '#ffffff',
            }}
          />
        </label>
        {error && (
          <div style={{ fontSize: '0.85rem', color: '#c0392b' }}>{error}</div>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
